import type { TOptionValue } from './Types';

export const createValueRecord = <T extends string | number>(
  values: T[],
  options: Map<T, string>,
) =>
  values.reduce(
    (record, val) => {
      const label = options.get(val);
      if (label !== undefined) {
        record[val] = label;
      }
      return record;
    },
    {} as Record<T, string>,
  );

/**
 * Converts a record to a sorted array to ensure consistent ordering.
 * This ensures the array order remains stable when toggling items on/off.
 *
 * Sorting behavior:
 * - Numeric values are sorted numerically (0, 1, 2, 3...)
 * - String values are sorted alphabetically
 *
 * Type conversion:
 * - Numeric strings (e.g., '1', '2', '3') are automatically converted to numbers
 * - This maintains backward compatibility with existing behavior
 * - If T is string but values are numeric strings, the returned type will be number
 *
 * @param record - The record of selected values (keys are values, values are labels)
 * @returns Array of selected options sorted by value
 *
 * @example
 * // Days of week (numeric values)
 * const days = toSortedArray({ 2: 'Tue', 0: 'Sun', 4: 'Thu' });
 * // Returns: [{ value: 0, label: 'Sun' }, { value: 2, label: 'Tue' }, { value: 4, label: 'Thu' }]
 *
 * @example
 * // Benefits (string values)
 * const benefits = toSortedArray({ 'Flexible Schedule': 'Flexible Schedule', 'Dental Insurance': 'Dental Insurance' });
 * // Returns: [{ value: 'Dental Insurance', ... }, { value: 'Flexible Schedule', ... }] (alphabetical)
 */
export const toSortedArray = <T extends string | number>(
  record: Record<T, string>,
): TOptionValue<T | number>[] => {
  // Convert record entries to array
  const result = (Object.entries(record) as [string, string][]).map(([key, label]) => ({
    // Convert numeric strings to numbers for consistent comparison
    value: (!isNaN(Number(key)) ? Number(key) : key) as T | number,
    label,
  }));

  // Sort by value to ensure consistent ordering
  return result.sort((a, b) => {
    if (typeof a.value === 'number' && typeof b.value === 'number') {
      return a.value - b.value;
    }
    // For strings, sort alphabetically
    return String(a.value).localeCompare(String(b.value));
  });
};
